import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  InputSearch,
  TableData,
  Pagination,
  DateRangeFilter,
  UniversalFilter,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import {
  Column,
  BusinessUnit,
  BUSINESS_UNIT_LABELS,
  getBusinessUnitLabel,
  Oem,
  Model,
} from '@/types/common';
import {
  VehicleAlert,
  VehicleAlertStatus,
  AlertType,
  VehicleAlertListRequest,
  getVehicleAlerts,
  getAlertTypeLabel,
} from '@/services/api/monitoring';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import { formatDateTime, formatDuration } from './monitoringFormat';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface MonitoringFilters {
  alertTypes?: AlertType[];
  businessUnits?: string[];
  oemIDs?: string[];
  modelIDs?: string[];
}

const STATUS_TABS: { key: VehicleAlertStatus; label: string }[] = [
  { key: VehicleAlertStatus.OPEN, label: 'Open' },
  { key: VehicleAlertStatus.RESOLVED, label: 'Resolved' },
];

const toISODate = (date: Date, endOfDay = false): string => {
  const d = new Date(date);
  if (endOfDay) {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return d.toISOString();
};

const MonitoringPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('firedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(
    'desc'
  );

  const [activeFilters, setActiveFilters] = useState<MonitoringFilters>({});
  const [dateRange, setDateRange] = useState<DateRange>({
    from: null,
    to: null,
  });
  const [statusTab, setStatusTab] = useState<VehicleAlertStatus>(
    VehicleAlertStatus.OPEN
  );

  const [alerts, setAlerts] = useState<VehicleAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);

  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [oemRes, modelRes] = await Promise.all([
          getListOem({ page: '1', size: '1000' }),
          getListModel({ page: '1', size: '1000' }),
        ]);
        setOems(oemRes.data || []);
        setModels(modelRes.data || []);
      } catch (error) {
        console.error('Failed to load OEM/model reference data:', error);
      }
    };
    loadReferenceData();
  }, []);

  const handleRowClick = useCallback(
    (record: VehicleAlert) => {
      navigate(`/monitoring/${record.ID}`, { state: { alert: record } });
    },
    [navigate]
  );

  const fetchAlerts = useCallback(
    async (
      page: number,
      search: string,
      filters: MonitoringFilters,
      size: number,
      range: DateRange,
      sort: string,
      order: 'asc' | 'desc' | null,
      status: VehicleAlertStatus
    ) => {
      setIsLoading(true);
      try {
        const params: VehicleAlertListRequest = {
          page: String(page),
          size: String(size),
          sort: sort || 'firedAt',
          order: order || 'desc',
          search: search || undefined,
          status,
          alertTypes: filters.alertTypes?.length
            ? filters.alertTypes.join(',')
            : undefined,
          businessUnits: filters.businessUnits?.length
            ? filters.businessUnits.join(',')
            : undefined,
          oemIDs: filters.oemIDs?.length ? filters.oemIDs.join(',') : undefined,
          modelIDs: filters.modelIDs?.length
            ? filters.modelIDs.join(',')
            : undefined,
          firedAtStart: range?.from ? toISODate(range.from) : undefined,
          firedAtEnd: range?.to ? toISODate(range.to, true) : undefined,
        };

        const response = await getVehicleAlerts(params);
        setAlerts(response.data || []);
        setTotal(response.pagination?.total || 0);
        setOpenCount(response.openCount || 0);
        setResolvedCount(response.resolvedCount || 0);
      } catch (error) {
        console.error('Failed to fetch vehicle alerts:', error);
        setAlerts([]);
        setTotal(0);
        setOpenCount(0);
        setResolvedCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchAlerts(
      currentPage,
      searchTerm,
      activeFilters,
      pageSize,
      dateRange,
      sortField,
      sortDirection,
      statusTab
    );
  }, [
    fetchAlerts,
    currentPage,
    searchTerm,
    activeFilters,
    pageSize,
    dateRange,
    statusTab,
    sortField,
    sortDirection,
  ]);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'alertTypes',
        title: 'Alert Type',
        icon: 'ri-alarm-warning-line',
        multiple: true,
        dataSource: [
          { id: AlertType.TELEMETRY_STALE, name: 'Telemetry Stale' },
        ],
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-building-line',
        multiple: true,
        dataSource: Object.values(BusinessUnit).map((bu) => ({
          id: bu,
          name: BUSINESS_UNIT_LABELS[bu] || bu,
        })),
        labelField: 'name',
        valueField: 'id',
      },
      {
        key: 'oemIDs',
        title: 'OEM',
        icon: 'ri-building-2-line',
        multiple: true,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelIDs',
        title: 'Model',
        icon: 'ri-car-line',
        multiple: true,
        dataSource: models,
        labelFormatter: (model: Model) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
    ],
    [oems, models]
  );

  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  }, []);

  const handleSort = (field: string, direction: 'asc' | 'desc' | null) => {
    // The API only supports sorting by firedAt, resolvedAt and createdAt.
    const sortableByApi = ['firedAt', 'resolvedAt', 'createdAt'];
    if (!sortableByApi.includes(field)) return;
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleDateRangeChange = useCallback((range: DateRange) => {
    setDateRange(range);
    setCurrentPage(1);
  }, []);

  const handleFiltersApply = useCallback((filters: MonitoringFilters) => {
    setActiveFilters(filters);
    setCurrentPage(1);
  }, []);

  const handleFiltersClear = useCallback(() => {
    setActiveFilters({});
    setCurrentPage(1);
  }, []);

  const handleTabChange = useCallback((tab: VehicleAlertStatus) => {
    setStatusTab(tab);
    setCurrentPage(1);
  }, []);

  const tabCounts: Record<VehicleAlertStatus, number> = {
    [VehicleAlertStatus.OPEN]: openCount,
    [VehicleAlertStatus.RESOLVED]: resolvedCount,
  };

  const columns: Column<VehicleAlert>[] = [
    {
      key: 'vehicle',
      title: 'Vehicle',
      render: (_, record) => (
        <div className="text-[14px] font-medium">
          {record.platNomor || '-'}
        </div>
      ),
    },
    {
      key: 'oemName',
      title: 'OEM',
      render: (value: string | null) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'modelName',
      title: 'Model',
      render: (value: string | null) => (
        <span className="text-[14px]">{value || '-'}</span>
      ),
    },
    {
      key: 'businessUnit',
      title: 'Business Unit',
      render: (value: string | null) => (
        <span className="text-[14px]">{getBusinessUnitLabel(value)}</span>
      ),
    },
    {
      key: 'alertType',
      title: 'Alert Type',
      render: (value: string) => (
        <span className="text-[14px]">{getAlertTypeLabel(value)}</span>
      ),
    },
    {
      key: 'firedAt',
      title: 'Fired At',
      sortable: true,
      render: (value: string) => (
        <span className="text-[14px]">{formatDateTime(value)}</span>
      ),
    },
    ...(statusTab === VehicleAlertStatus.RESOLVED
      ? [
          {
            key: 'resolvedAt',
            title: 'Resolved At',
            sortable: true,
            render: (value: string | null) => (
              <span className="text-[14px]">{formatDateTime(value)}</span>
            ),
          } as Column<VehicleAlert>,
        ]
      : []),
    {
      key: 'openFor',
      title: 'Open For',
      align: 'center',
      render: (_: unknown, record: VehicleAlert) => (
        <span className="text-[14px] font-medium text-gray-900">
          {formatDuration(record.durationMinutes)}
        </span>
      ),
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<VehicleAlert>({
    storageKey: 'monitoring',
    columns,
  });

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Monitoring
            </h1>
            <p className="text-[14px] text-gray-600">
              Vehicle monitoring alerts across the fleet.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {STATUS_TABS.map((tab) => {
            const isActive = statusTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-2 border-b-2 px-1 py-3 text-[14px] font-medium transition-colors ${
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                    isActive
                      ? 'bg-purple-100 text-primary'
                      : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {tabCounts[tab.key]}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center">
            <DateRangeFilter
              onDateRangeChange={handleDateRangeChange}
              initialRange={dateRange}
            />
          </div>

          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search plat nomor…"
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      {searchTerm && !isLoading && (
        <div className="mb-3 px-2">
          <p className="text-sm text-gray-600">
            Found <span className="font-semibold">{total}</span> result
            {total !== 1 ? 's' : ''} for{' '}
            <span className="font-semibold">'{searchTerm}'</span>
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="relative">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <span className="ml-2 text-gray-600">Loading alerts...</span>
            </div>
          ) : (
            <TableData
              data={alerts}
              columns={visibleColumns}
              sorting={{
                field: sortField,
                direction: sortDirection,
                onSort: handleSort,
              }}
              onRowClick={handleRowClick}
              size="small"
              className="min-w-full"
            />
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(newSize) => {
          setPageSize(newSize);
          setCurrentPage(1);
        }}
      />
    </div>
  );
};

export default MonitoringPage;
